import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import type { SaveRecurringShiftHoursRequest } from "../../../../services/backend.service";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { formatSlots } from "./recurringDisplay";

type Props = {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
	/** Exactly what will be sent — every selected job with the hours on screen. */
	jobs: SaveRecurringShiftHoursRequest["jobs"];
	saving: boolean;
};

/**
 * Confirms a save to Department Configuration by listing every job it would write.
 *
 * Listed in full because pre-ticked jobs can sit behind the panel's collapse toggle: without the list
 * an operator could save hours for a job they never saw ticked.
 */
export const RecurringShiftHoursSaveDialog: FC<Props> = ({ open, onClose, onConfirm, jobs, saving }) => {
	const { t } = useTranslation();
	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("onboarding_load.recurring_shift_hours_save_dialog_title")}
			maxWidth="sm"
			fullWidth
			confirmButtonVariant="primary"
			confirm={t("onboarding_load.recurring_shift_hours_save_dialog_confirm", { count: jobs.length })}
			confirmDisabled={jobs.length === 0}
			confirmLoading={saving}
			onConfirm={onConfirm}
			dataTestId="recurring-shift-hours-save-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
						{t("onboarding_load.recurring_shift_hours_save_dialog_description", { count: jobs.length })}
					</Typography>
					<Box component="ul" sx={{ m: 0, pl: 2.5 }} data-testid="recurring-shift-hours-save-dialog-jobs">
						{jobs.map((job) => (
							<Typography component="li" key={job.jobName} sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
								<strong>{job.jobName}:</strong> {formatSlots(job.slots)}
							</Typography>
						))}
					</Box>
					<InlineHint tone="info">{t("onboarding_load.recurring_shift_hours_save_dialog_keep_existing")}</InlineHint>
				</Box>
			}
		/>
	);
};
